import { useCallback, useRef, useState } from "react";
import { api } from "../api/client.js";
import { draftMeeting } from "../utils/anthropicClient.js";
import { buildPacket } from "./packet.js";
import { planDraft } from "./applyDraft.js";
import { getGraphToken, createGraphGet, connectMicrosoft365 } from "./graph.js";
import { collectFileActivity } from "./sharepoint.js";
import { collectClientEmails } from "./mail.js";

// The meeting whose Next steps carry forward: strictly earlier, on the same project when this
// meeting has one (other projects only if none exist), and an update over a kickoff unless a
// kickoff is all there is.
export function pickPreviousMeeting(rows, meeting) {
  const earlier = rows
    .filter((m) => m.id !== meeting.id && (m.meetingDate < meeting.meetingDate || (m.meetingDate === meeting.meetingDate && (m.number || 0) < (meeting.number || 0))))
    .sort((a, b) => (a.meetingDate < b.meetingDate ? 1 : a.meetingDate > b.meetingDate ? -1 : (b.number || 0) - (a.number || 0)));
  const sameProject = meeting.projectId ? earlier.filter((m) => m.projectId === meeting.projectId) : [];
  const pool = sameProject.length ? sameProject : earlier;
  return pool.find((m) => m.type !== "kickoff") || pool[0] || null;
}

const REVIEW_BEFORE_SENDING = "New draft content needs review before this meeting goes out.";
const CONNECT_NOTICE = "SharePoint and email weren’t included — use “Connect Microsoft 365” to add them.";
const SHAREPOINT_NO_RESPONSE = "SharePoint skipped — SharePoint didn’t respond.";
const EMAIL_NO_RESPONSE = "Email skipped — your mailbox didn’t respond (unexpected error).";
const EMAIL_NO_CLIENT = "Email skipped — couldn’t load the client’s contacts.";
const GATHER_MS = 45000; // shared budget for SharePoint + email before the draft starts
const EMAIL_TOO_SLOW = "Email skipped — your mailbox took longer than 45 seconds.";

// A Graph collector must never fail Generate: any unexpected throw (beyond the notices the
// collectors already produce themselves for Graph-level failures) falls back to the same shape
// the collector itself would have returned, carrying its own "didn’t respond" notice.
const safeCollect = async (fn, fallback) => {
  try { return await fn(); } catch { return fallback; }
};

// The proxy's own messages (refused, bad_output, timeout, upstream_error, no_key) are already
// written for users; these two are not.
function draftError(res) {
  if (res.status === 401) return "Your sign-in has expired — sign in again and retry.";
  if (res.data?.code === "invalid_packet") return "Couldn’t prepare this client’s data for drafting.";
  return res.data?.error || "Couldn’t generate a draft — try again.";
}

// Generate for MeetingView: gather → draft (proxy) → write sections → stamp. Nothing is written
// unless the draft succeeds; a section whose save conflicts is skipped and named in the notices.
export function useGenerate({ meeting, clientName, objectives, tasks, proposals, crm = null, onMeeting }) {
  const [generating, setGenerating] = useState(false);
  const [notices, setNotices] = useState([]);
  const [needsConnect, setNeedsConnect] = useState(false);
  // A synchronous re-entry guard: state updates aren't visible until the next render, so a second
  // call fired before that (e.g. a Regenerate click racing the header button) must be rejected here.
  const runningRef = useRef(false);

  const generate = useCallback(async ({ only = null, mode = "replace" } = {}) => {
    if (runningRef.current) return { ok: false, error: "A draft is already being generated." };
    runningRef.current = true;
    setGenerating(true);
    try {
      const [notes, list, client, filesTok, mailTok] = await Promise.all([
        api.clients.notes.list(meeting.clientId), api.meetings.list(meeting.clientId), api.clients.get(meeting.clientId).catch(() => null),
        getGraphToken("files"), getGraphToken("mail"),
      ]);
      const gatherNotices = [];
      let files = [], emails = [], links = new Map();
      // Every run reports current reality: a later run with both scopes working must hide the
      // Connect button, not just leave it stuck true from an earlier failed run.
      setNeedsConnect(!filesTok.ok || !mailTok.ok);
      if (!filesTok.ok || !mailTok.ok) gatherNotices.push(CONNECT_NOTICE);
      if (client == null) {
        // The client fetch itself failed — collectFileActivity/collectClientEmails would otherwise
        // read `undefined`/`[]` off a missing client and wrongly report "no folder link" / "no
        // contacts", which isn't true. Say the real reason instead, and don't call them at all.
        if (filesTok.ok) gatherNotices.push(SHAREPOINT_NO_RESPONSE);
        if (mailTok.ok) gatherNotices.push(EMAIL_NO_CLIENT);
      } else {
        // A shared 45s deadline for both collectors: collectFileActivity stops starting new
        // Graph requests and returns a partial result once it passes; the mail collector has no
        // equivalent internal cutoff, so it's raced against the same deadline here instead.
        const deadline = Date.now() + GATHER_MS;
        const raceMail = (p) => Promise.race([
          p,
          new Promise((resolve) => setTimeout(() => resolve({ emails: [], notice: EMAIL_TOO_SLOW }), Math.max(0, deadline - Date.now()))),
        ]);
        const [fileRes, mailRes] = await Promise.all([
          filesTok.ok ? safeCollect(() => collectFileActivity({ graphGet: createGraphGet(filesTok.token), sharingUrl: client?.sharepointUrl, periodStart: meeting.periodStart, periodEnd: meeting.periodEnd, deadline }), { files: [], links: new Map(), notice: SHAREPOINT_NO_RESPONSE }) : null,
          mailTok.ok ? safeCollect(() => raceMail(collectClientEmails({ graphGet: createGraphGet(mailTok.token), pocs: client?.pocs || [], periodStart: meeting.periodStart, periodEnd: meeting.periodEnd })), { emails: [], notice: EMAIL_NO_RESPONSE }) : null,
        ]);
        if (fileRes) { files = fileRes.files; links = fileRes.links; if (fileRes.notice) gatherNotices.push(fileRes.notice); }
        if (mailRes) { emails = mailRes.emails; if (mailRes.notice) gatherNotices.push(mailRes.notice); }
      }

      const prevRow = pickPreviousMeeting(list?.items || [], meeting);
      const previousMeeting = prevRow ? await api.meetings.get(prevRow.id) : null;
      const packet = buildPacket({ clientName, meeting, objectives, tasks, proposals, notes: Array.isArray(notes) ? notes : [], previousMeeting, files, emails, crm });
      const res = await draftMeeting(packet);
      if (!res.ok) return { ok: false, error: draftError(res) };

      // SharePoint links never entered the packet; attach them to file sources now, before planning
      // what gets written, so a link survives into the saved section without ever reaching the proxy.
      for (const items of Object.values(res.data.sections || {})) {
        for (const it of items || []) {
          for (const s of it.sources || []) {
            if (s.kind === "file" && links.has(s.ref)) s.url = links.get(s.ref);
          }
        }
      }

      const plan = planDraft(meeting, res.data, { only, mode });
      const out = [...gatherNotices, ...(res.data.notices || []), ...plan.skipped.map((s) => `Skipped: ${s}.`)];
      let latest = meeting;
      let wroteAny = false;
      for (const w of plan.writes) {
        const r = w.sectionId
          ? await api.meetings.saveSection(meeting.id, w.sectionId, w.rev, { title: w.title, items: w.items })
          : await api.meetings.addSection(meeting.id, { title: w.title, items: w.items });
        if (r.ok) { latest = r.data; wroteAny = true; }
        else out.push(r.status === 409 ? `“${w.title}” was changed by someone else while drafting — it was left as it was.` : `“${w.title}” couldn’t be saved: ${r.data?.error || "try again"}.`);
      }
      if (plan.headline) {
        // Use the pre-generation snapshot rev/hiddenTiles, not `latest`: section writes never bump
        // the summary rev, so the snapshot rev is right when nothing changed, and 409s (rather than
        // silently overwriting) when someone edited the headline while the draft was generating.
        const r = await api.meetings.saveBlock(meeting.id, "summary", meeting.summary.rev, { headline: plan.headline, hiddenTiles: meeting.summary.hiddenTiles });
        if (r.ok) { latest = r.data; wroteAny = true; } else out.push("The headline was changed by someone else while drafting — it was left as it was.");
      }
      // Status is left alone (the user decides), but a meeting already marked ready or presented
      // now carries unreviewed text, so say so — and persist it with the stamp below.
      if (wroteAny && (meeting.status === "ready" || meeting.status === "presented")) out.push(REVIEW_BEFORE_SENDING);
      if (wroteAny) {
        const stamp = await api.meetings.setGeneration(meeting.id, out);
        if (stamp.ok) latest = stamp.data;
        else out.push("The draft was applied, but its notes couldn’t be saved.");
        onMeeting(latest);
      }
      // Only called above, on an actual write: when every target conflicted, `latest` is just the
      // pre-generation snapshot, and handing that back would revert anything the caller adopted
      // (its own saves, a focus reload) during the ~1-minute draft call.
      setNotices(out);
      return { ok: true, wrote: wroteAny, notices: out };
    } catch {
      return { ok: false, error: "Couldn’t gather this client’s data — try again." };
    } finally {
      setGenerating(false);
      runningRef.current = false;
    }
  }, [meeting, clientName, objectives, tasks, proposals, crm, onMeeting]);

  const connect = useCallback(async () => {
    const r = await connectMicrosoft365();
    if (r.ok) setNeedsConnect(false);
    return r;
  }, []);

  return { generate, generating, notices, needsConnect, connect };
}
